// Phase 8 slice 1 — Irrigation Intelligence page.
//
// Tonight's synthetic prescription per station + water budget card.
// Admin actions: seed stations + manually trigger generation. Real Hunter
// Pilot data lands as source='real' rows in the same table; the amber
// Demo Data ribbon signals the difference.

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { IrrigationPrescription, UserRole, WaterBudget } from '@greeniq/shared';
import { ApiError } from '../api/client.js';
import {
  acknowledgeIrrigationAlert,
  generateIrrigationRx,
  getIrrigationAlerts,
  getPrescriptions,
  getRuntimeHeatmap,
  seedDemoStations,
  type HeatmapResponse,
  type IrrigationAlert,
} from '../api/irrigation.js';
import { useMe } from '../context/MeContext.js';

export function IrrigationPage() {
  const { courseSlug } = useParams();
  const { me } = useMe();
  const role: UserRole | undefined = me?.courses.find((c) => c.courseSlug === courseSlug)?.role;
  const isAdmin = role === 'ageye_admin';

  const [forDate, setForDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [prescriptions, setPrescriptions] = useState<IrrigationPrescription[] | null>(null);
  const [waterBudget, setWaterBudget] = useState<WaterBudget | null>(null);
  const [alerts, setAlerts] = useState<IrrigationAlert[]>([]);
  const [heatmap, setHeatmap] = useState<HeatmapResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function load() {
    if (!courseSlug) return;
    try {
      const [rx, al, hm] = await Promise.all([
        getPrescriptions(courseSlug, forDate),
        getIrrigationAlerts(courseSlug),
        getRuntimeHeatmap(courseSlug, 30),
      ]);
      setPrescriptions(rx.prescriptions);
      setWaterBudget(rx.waterBudget);
      setAlerts(al.alerts);
      setHeatmap(hm);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  async function ackAlert(id: number) {
    if (!courseSlug) return;
    try {
      await acknowledgeIrrigationAlert(courseSlug, id);
      setAlerts((prev) => prev.filter((a) => a.id !== id));
    } catch { /* swallow */ }
  }

  useEffect(() => {
    void load();
  }, [courseSlug, forDate]);

  async function onSeed() {
    if (!courseSlug) return;
    setMessage(null);
    try {
      const r = await seedDemoStations(courseSlug);
      setMessage(
        `Seeded ${r.inserted} station${r.inserted === 1 ? '' : 's'} (${r.existing} existing) across ${r.zonesTouched} zones.`,
      );
    } catch (err) {
      setMessage(`Seed failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  async function onGenerate() {
    if (!courseSlug) return;
    setMessage(null);
    try {
      const r = await generateIrrigationRx(courseSlug, forDate);
      setMessage(`Generated ${r.stations} prescriptions for ${r.forDate}.`);
      await load();
    } catch (err) {
      setMessage(`Generate failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  if (error) return <div className="p-8 text-[color:var(--color-error)]">{error}</div>;

  const totalRuntime = (prescriptions ?? []).reduce((s, p) => s + Number(p.runtimeMinutes), 0);
  const totalGal = (prescriptions ?? []).reduce((s, p) => s + Number(p.waterAppliedGal ?? 0), 0);

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="uppercase tracking-wider text-xs text-greeniq-400 font-semibold">Phase 8</p>
          <h1 className="mt-1 text-3xl md:text-4xl font-display font-bold text-fg">Irrigation Intelligence</h1>
          <p className="mt-1 text-fg-muted">
            Tonight's prescription per station, derived from 3-day ET deficit × Kc × zone area. Synthetic until Hunter Pilot integration lands.
          </p>
        </div>
        {isAdmin && (
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => void onSeed()}
              className="px-3 py-2 rounded-md bg-bg-hover text-greeniq-300 text-xs font-semibold hover:bg-bg-elevated"
            >
              Seed stations
            </button>
            <button
              onClick={() => void onGenerate()}
              className="px-3 py-2 rounded-md bg-greeniq-500 text-[#0b1014] text-xs font-semibold hover:bg-greeniq-400"
            >
              Generate now
            </button>
          </div>
        )}
      </div>

      {message && <div className="mt-4 p-3 rounded-md bg-bg-elevated text-sm text-fg">{message}</div>}

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <label className="text-xs text-fg-muted">
          Date
          <input
            type="date"
            value={forDate}
            onChange={(e) => setForDate(e.target.value)}
            className="ml-2 border border-border rounded-md py-1.5 px-2 text-sm"
          />
        </label>
      </div>

      {/* Tonight summary + water budget */}
      <div className="mt-6 grid md:grid-cols-3 gap-4">
        <SummaryTile
          label="Tonight runtime"
          value={formatRuntime(totalRuntime)}
          detail={`${prescriptions?.length ?? 0} stations cycling`}
        />
        <SummaryTile
          label="Water tonight"
          value={totalGal > 0 ? `${Math.round(totalGal).toLocaleString()} gal` : '—'}
          detail={totalGal > 0 ? `${(totalGal / 7480.52).toFixed(2)} ft³` : 'ET deficit ≤ rainfall'}
        />
        <WaterBudgetTile budget={waterBudget} />
      </div>

      {alerts.length > 0 && (
        <section className="mt-6 p-4 bg-bg-card rounded-lg border border-[color:var(--color-warning)]/30">
          <h2 className="text-sm uppercase tracking-wider text-[color:var(--color-warning)] font-semibold">
            Runtime-spike alerts · {alerts.length} unacknowledged
          </h2>
          <ul className="mt-3 divide-y divide-amber-100">
            {alerts.map((a) => {
              const observed = Number(a.observedMinutes);
              const baseline = Number(a.baselineMinutes);
              const mult = baseline > 0 ? observed / baseline : 0;
              return (
                <li key={a.id} className="py-2 flex items-center gap-3 flex-wrap text-sm">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${a.severity === 'critical' ? 'bg-error-muted text-[color:var(--color-error)]' : 'bg-warning-muted text-[color:var(--color-warning)]'}`}>
                    {a.severity}
                  </span>
                  <span className="font-mono text-xs">{a.stationNumber ?? `#${a.stationId}`}</span>
                  <span>{a.zoneName ?? 'course-wide'}</span>
                  <span className="text-fg">
                    Runtime <strong>{Math.round(observed)}min</strong> · baseline {Math.round(baseline)}min · <strong>{mult.toFixed(1)}×</strong>
                  </span>
                  <button
                    onClick={() => void ackAlert(a.id)}
                    className="ml-auto text-xs px-2 py-1 rounded-md bg-bg-hover text-greeniq-300 font-semibold hover:bg-bg-elevated"
                  >
                    Acknowledge
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {heatmap && heatmap.stations.length > 0 && <RuntimeHeatmap heatmap={heatmap} />}

      {/* Per-station prescription list */}
      <div className="mt-8 overflow-auto bg-bg-card rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-border bg-bg-elevated">
              <th className="px-3 py-2 font-semibold text-fg-muted">Station</th>
              <th className="px-3 py-2 font-semibold text-fg-muted">Zone</th>
              <th className="px-3 py-2 font-semibold text-fg-muted text-right">Runtime</th>
              <th className="px-3 py-2 font-semibold text-fg-muted text-right">ET deficit</th>
              <th className="px-3 py-2 font-semibold text-fg-muted text-right">Water</th>
              <th className="px-3 py-2 font-semibold text-fg-muted">Rationale</th>
            </tr>
          </thead>
          <tbody>
            {(prescriptions ?? []).length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-fg-muted">
                  {isAdmin
                    ? 'No prescriptions yet. Click "Seed stations" once zones exist, then "Generate now".'
                    : 'No prescriptions for this date yet.'}
                </td>
              </tr>
            )}
            {(prescriptions ?? []).map((p) => (
              <tr key={p.id} className="border-b border-border last:border-b-0 align-top">
                <td className="px-3 py-2 font-mono text-xs">{p.stationNumber ?? `#${p.stationId}`}</td>
                <td className="px-3 py-2">{p.zoneName ?? 'course-wide'}</td>
                <td className="px-3 py-2 text-right font-semibold">{formatRuntime(Number(p.runtimeMinutes))}</td>
                <td className="px-3 py-2 text-right">
                  {p.etDeficitIn !== null ? `${Number(p.etDeficitIn).toFixed(2)}″` : '—'}
                </td>
                <td className="px-3 py-2 text-right">
                  {p.waterAppliedGal !== null ? `${Math.round(Number(p.waterAppliedGal)).toLocaleString()} gal` : '—'}
                </td>
                <td className="px-3 py-2 text-xs text-fg-muted max-w-sm">
                  {renderRationale(p.rationale)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RuntimeHeatmap({ heatmap }: { heatmap: HeatmapResponse }) {
  const cellByKey = new Map<string, number>();
  let maxRuntime = 0;
  for (const c of heatmap.cells) {
    cellByKey.set(`${c.stationId}|${c.date}`, c.runtimeMinutes);
    if (c.runtimeMinutes > maxRuntime) maxRuntime = c.runtimeMinutes;
  }
  const cellColor = (runtime: number): string => {
    // Dark-theme scale: dim teal → bright greeniq-500 as runtime rises.
    if (runtime <= 0) return 'rgba(255, 255, 255, 0.04)';
    const t = Math.min(1, runtime / Math.max(maxRuntime, 60));
    // Base: deep teal #0a3a30, target: greeniq-500 #22d47a.
    const r = Math.round(10 + (34 - 10) * t);
    const g = Math.round(58 + (212 - 58) * t);
    const b = Math.round(48 + (122 - 48) * t);
    const alpha = 0.35 + 0.55 * t;
    return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(2)})`;
  };

  return (
    <section className="mt-6 p-4 bg-bg-card rounded-lg border border-border">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm uppercase tracking-wider text-fg-muted font-semibold">
          30-day runtime heatmap
        </h2>
        <span className="text-[10px] text-fg-muted">minutes per station per night · darker = longer</span>
      </div>
      <div className="mt-3 overflow-auto">
        <table className="text-[10px] border-collapse">
          <thead>
            <tr>
              <th className="sticky left-0 bg-bg-card z-10 text-left px-2 py-1 text-fg-muted font-semibold">Station</th>
              {heatmap.dates.map((d) => {
                const day = d.slice(8, 10);
                const showMonth = day === '01';
                return (
                  <th key={d} className="px-0.5 py-1 text-center text-fg-muted font-normal">
                    {showMonth ? d.slice(5, 7) + '-' + day : day}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {heatmap.stations.map((s) => (
              <tr key={s.id}>
                <td className="sticky left-0 bg-bg-card z-10 px-2 py-0.5 whitespace-nowrap font-mono">
                  {s.stationNumber}
                  <span className="ml-1 text-fg-muted">· {s.zoneName ?? 'n/a'}</span>
                </td>
                {heatmap.dates.map((d) => {
                  const runtime = cellByKey.get(`${s.id}|${d}`) ?? 0;
                  return (
                    <td
                      key={d}
                      className="w-4 h-4 border border-bg-card"
                      title={`${s.stationNumber} · ${d} · ${Math.round(runtime)}min`}
                      style={{ background: cellColor(runtime) }}
                    />
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SummaryTile({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div className="p-4 bg-bg-card rounded-lg border border-border">
      <div className="text-xs uppercase tracking-wider text-fg-muted">{label}</div>
      <div className="mt-1 text-2xl font-display font-semibold text-greeniq-300">{value}</div>
      <div className="mt-1 text-xs text-fg-muted">{detail}</div>
    </div>
  );
}

function WaterBudgetTile({ budget }: { budget: WaterBudget | null }) {
  if (!budget) {
    return (
      <div className="p-4 bg-bg-card rounded-lg border border-dashed border-border">
        <div className="text-xs uppercase tracking-wider text-fg-muted">Water budget</div>
        <div className="mt-1 text-sm text-fg-muted">
          No budget set for {new Date().getFullYear()}. An admin can add the annual permit allocation.
        </div>
      </div>
    );
  }
  const used = Number(budget.usedGallons);
  const permit = budget.permitGallons !== null ? Number(budget.permitGallons) : null;
  const pct = permit !== null && permit > 0 ? (used / permit) * 100 : null;
  return (
    <div className="p-4 bg-bg-card rounded-lg border border-border">
      <div className="text-xs uppercase tracking-wider text-fg-muted">
        {budget.year} water used
      </div>
      <div className="mt-1 text-2xl font-display font-semibold text-greeniq-300">
        {Math.round(used).toLocaleString()} gal
      </div>
      {permit !== null && (
        <>
          <div className="mt-1 text-xs text-fg-muted">
            of {Math.round(permit).toLocaleString()} gal permit ({pct !== null ? `${pct.toFixed(1)}%` : '—'})
          </div>
          {pct !== null && (
            <div className="mt-2 h-2 bg-bg-hover rounded-full overflow-hidden">
              <div
                className={pct > 85 ? 'h-full bg-[color:var(--color-error)]' : pct > 65 ? 'h-full bg-[color:var(--color-warning)]' : 'h-full bg-greeniq-600'}
                style={{ width: `${Math.min(100, pct)}%` }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function formatRuntime(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes <= 0) return 'Skip';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function renderRationale(rationale: Record<string, unknown> | null): string {
  if (!rationale) return '';
  const et = rationale.etTotalIn as number | undefined;
  const precip = rationale.precipTotalIn as number | undefined;
  const inches = rationale.inchesToApply as number | undefined;
  const kc = rationale.kc as number | undefined;
  const parts: string[] = [];
  if (et !== undefined) parts.push(`ET ${et.toFixed(2)}″`);
  if (precip !== undefined) parts.push(`rain ${precip.toFixed(2)}″`);
  if (kc !== undefined) parts.push(`Kc ${kc.toFixed(2)}`);
  if (inches !== undefined) parts.push(`→ apply ${inches.toFixed(2)}″`);
  return parts.join(' · ');
}
